import { useQuery } from '@apollo/client';
import { getDataFromTree } from '@apollo/client/react/ssr';
// Types
import { NextPage } from 'next';
import { ApolloPageContext, PageProps } from 'helpers/next-page';
import { GET_MEDIA_POSTS } from 'api/mediaPost/queries';
import {
  GetMediaPosts,
  GetMediaPostsVariables,
} from 'api/mediaPost/types/GetMediaPosts';
import { MediaPostStatus } from 'api/graphql-global-types';
// Helpers
import { getAthleteTabMetaTitle } from 'helpers/athleteTabSeo';
import { getAthleteTabPageProps } from 'helpers/athleteTabPageGuard';
import { getEnvLink } from 'helpers/routes';
import { generateItemListStructuredData } from 'helpers/structured-data';
// Hocks
import withApollo from 'hocs/withApollo';
// Hooks
import {
  useRedirectOnError,
  useRedirectIfNoContent,
  useAthletePageData,
} from 'hooks';
// Layouts
import { getProfileAvatarPreload } from 'helpers/profileAvatar';
import Head from 'layouts/Head/Head';
import Header from 'layouts/Header/Header';
import Main from 'layouts/Main/Main';
import Footer from 'layouts/Footer/Footer';
// Components
import AppBannerAndModal from 'components/common3/AppBannerAndModal/AppBannerAndModal';
import Description from 'components/Athlete/Description/Description';
import MediaPostLibrary from 'components/MediaPostLibrary/MediaPostLibrary';
import NotFoundPage from 'components/NotFoundPage/NotFoundPage';

const MediaFeedContent = (): JSX.Element | null => {
  const {
    error,
    skip,
    cleanedSlug,
    athleteSlug,
    metaData,
    store,
  } = useAthletePageData();

  const { data: mediaPostsData } = useQuery<
    GetMediaPosts,
    GetMediaPostsVariables
  >(GET_MEDIA_POSTS, {
    variables: {
      input: {
        storeId: store?.id || '',
        statuses: [MediaPostStatus.published],
      },
    },
    skip: !store,
  });

  const mediaPosts = mediaPostsData?.getMediaPosts?.entities;

  useRedirectOnError({ error, redirectTo: `/${athleteSlug}` });

  // Redirect to profile home if media content is not available
  useRedirectIfNoContent({
    hasContent: store?.hasMediaPost ?? false,
    redirectTo: `/${athleteSlug}`,
    skip: !store || skip,
  });

  if (!store) {
    return null;
  }

  const canonicalLink = `${getEnvLink()}/${cleanedSlug}/media`;

  const mediaItems =
    mediaPosts?.map((item) => {
      const isVideo = 'video' in item;
      const basePath = isVideo
        ? `social-media-post/video`
        : `social-media-post`;
      return {
        url: `${getEnvLink()}/${item.store.slug}/${basePath}/${item.slug}`,
      };
    }) || [];

  const structuredData = generateItemListStructuredData(
    `${metaData.name} Media`,
    mediaItems
  );

  return (
    <>
      <Head
        title={getAthleteTabMetaTitle(metaData.name, 'media')}
        description={`View ${metaData.name}'s latest photos and videos on MILLIONS.`}
        image={metaData.avatarUrl}
        imagePreload={getProfileAvatarPreload(metaData.avatarUrl)}
        canonicalLink={canonicalLink}
        robots={store.hasMediaPost ? 'index, follow' : 'noindex, follow'}
        structuredData={structuredData}
      />
      <AppBannerAndModal
        when
        variant="profile"
        username={cleanedSlug ?? undefined}
        profileImageUrl={metaData.avatarUrl ?? undefined}
      />
      <Header />

      <Main backgroundColor="background-dim" withoutSidesPadding>
        <Description store={store} />
        {store.hasMediaPost && (
          <MediaPostLibrary storeId={store.id} isPage={false} />
        )}
      </Main>

      <Footer />
    </>
  );
};

const MediaFeed: NextPage<PageProps> = ({ isNotFound }) =>
  isNotFound ? <NotFoundPage /> : <MediaFeedContent />;

MediaFeed.getInitialProps = async (
  ctx: ApolloPageContext
): Promise<PageProps> => getAthleteTabPageProps(ctx, 'media');

export default withApollo(MediaFeed, { getDataFromTree });
